import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid';
import { rebaseTransaction as _rebaseTransaction } from '../common/rebase-transaction';
import { convertSyncBlockPMNodeToSyncBlockData, createBodiedSyncBlockNode as _createBodiedSyncBlockNode } from '../utils/utils';
// A store manager responsible for the lifecycle and state management of source sync blocks in an editor instance.
// Designed to manage local in-memory state and synchronize with an external data provider.
// Supports create, flush, and delete operations for source sync blocks.
// Handles caching, debouncing updates, and publish/subscribe for local changes.
// Ensures consistency between local and remote state, and can be used in both editor and renderer contexts.
export var SourceSyncBlockStoreManager = /*#__PURE__*/function () {
  function SourceSyncBlockStoreManager(dataProvider) {
    var _this = this;
    _classCallCheck(this, SourceSyncBlockStoreManager);
    _defineProperty(this, "setPendingDeletion", function (Ids, value) {
      var syncBlock = _this.syncBlockCache.get(Ids.resourceId);
      if (syncBlock) {
        syncBlock.pendingDeletion = value;
      }
    });
    this.dataProvider = dataProvider;
    this.syncBlockCache = new Map();
  }
  return _createClass(SourceSyncBlockStoreManager, [{
    key: "isSourceBlock",
    value: function isSourceBlock(node) {
      return node.type.name === 'bodiedSyncBlock';
    }

    /**
     * Add/update a sync block node to/from the local cache
     * @param syncBlockNode - The sync block node to update
     */
  }, {
    key: "updateSyncBlockData",
    value: function updateSyncBlockData(syncBlockNode) {
      if (!this.isSourceBlock(syncBlockNode)) {
        throw new Error('Invalid sync block node type provided for updateSyncBlockData');
      }
      var _syncBlockNode$attrs = syncBlockNode.attrs,
        localId = _syncBlockNode$attrs.localId,
        resourceId = _syncBlockNode$attrs.resourceId;
      if (!localId || !resourceId) {
        throw new Error('Local ID or resource ID is not set');
      }
      var syncBlockData = convertSyncBlockPMNodeToSyncBlockData(syncBlockNode);
      this.syncBlockCache.set(resourceId, syncBlockData);
      return true;
    }

    /**
     * Save content of bodiedSyncBlock nodes in local cache to backend
     *
     * @returns true if saving all nodes successfully, false if fail to save some/all nodes
     */
  }, {
    key: "flush",
    value: (function () {
      var _flush = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var bodiedSyncBlockNodes, bodiedSyncBlockData, writeResults;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              if (this.dataProvider) {
                _context.next = 3;
                break;
              }
              throw new Error('Data provider not set');
            case 3:
              bodiedSyncBlockNodes = [];
              bodiedSyncBlockData = [];
              Array.from(this.syncBlockCache.values()).forEach(function (syncBlockData) {
                // Don't flush nodes that are waiting to be deleted to avoid nodes being re-created
                if (!syncBlockData.pendingDeletion) {
                  bodiedSyncBlockNodes.push({
                    type: 'bodiedSyncBlock',
                    attrs: {
                      localId: syncBlockData.blockInstanceId,
                      resourceId: syncBlockData.resourceId
                    }
                  });
                  bodiedSyncBlockData.push(syncBlockData);
                }
              });
              if (!(bodiedSyncBlockNodes.length === 0)) {
                _context.next = 8;
                break;
              }
              return _context.abrupt("return", Promise.resolve(true));
            case 8:
              _context.next = 10;
              return this.dataProvider.writeNodesData(bodiedSyncBlockNodes, bodiedSyncBlockData);
            case 10:
              writeResults = _context.sent;
              return _context.abrupt("return", writeResults.every(function (result) {
                return result.resourceId !== undefined;
              }));
            case 14:
              _context.prev = 14;
              _context.t0 = _context["catch"](0);
              return _context.abrupt("return", false);
            case 17:
            case "end":
              return _context.stop();
          }
        }, _callee, this, [[0, 14]]);
      }));
      function flush() {
        return _flush.apply(this, arguments);
      }
      return flush;
    }())
  }, {
    key: "setEditorView",
    value: function setEditorView(editorView) {
      this.editorView = editorView;
    }
  }, {
    key: "registerPendingCreation",
    value: function registerPendingCreation(resourceId) {
      this.pendingResourceId = resourceId;
    }

    /**
     * Register callback function (which inserts node, handles focus etc) to be used later when creation to backend succeed
     */
  }, {
    key: "registerCreationCallback",
    value: function registerCreationCallback(callback) {
      this.creationCallback = callback;
    }

    /**
     *  Fires callback to insert node (if creation is successful) and clears pending creation data
     * @param success
     */
  }, {
    key: "commitPendingCreation",
    value: function commitPendingCreation(success) {
      if (success && this.creationCallback) {
        this.creationCallback();
      }
      this.pendingResourceId = undefined;
      this.creationCallback = undefined;
    }

    /**
     *
     * @returns true if waiting for the result of saving new bodiedSyncBlock to backend
     */
  }, {
    key: "hasPendingCreation",
    value: function hasPendingCreation() {
      return !!this.pendingResourceId;
    }
  }, {
    key: "registerConfirmationCallback",
    value: function registerConfirmationCallback(callback) {
      var _this2 = this;
      this.confirmationCallback = callback;
      return function () {
        _this2.confirmationCallback = undefined;
      };
    }
  }, {
    key: "requireConfirmationBeforeDelete",
    value: function requireConfirmationBeforeDelete() {
      return !!this.confirmationCallback;
    }

    /**
     * @returns attributes for a new bodiedSyncBlock node
     */
  }, {
    key: "generateBodiedSyncBlockAttrs",
    value: function generateBodiedSyncBlockAttrs() {
      var _this$dataProvider;
      // eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
      var localId = uuid();
      var sourceId = (_this$dataProvider = this.dataProvider) === null || _this$dataProvider === void 0 ? void 0 : _this$dataProvider.getSourceId();
      if (!this.dataProvider || !sourceId) {
        throw new Error('Provider of sync block plugin is not set');
      }
      var resourceId = this.dataProvider.generateResourceId(sourceId, localId);
      return {
        resourceId: resourceId,
        localId: localId
      };
    }

    /**
     * Create a bodiedSyncBlock node with empty content to backend
     * @param attrs attributes Ids of the node
     */
  }, {
    key: "createBodiedSyncBlockNode",
    value: function createBodiedSyncBlockNode(attrs) {
      var _this3 = this;
      try {
        if (!this.dataProvider) {
          throw new Error('Data provider not set');
        }
        var resourceId = attrs.resourceId,
          blockInstanceId = attrs.localId;
        this.dataProvider.writeNodesData([_createBodiedSyncBlockNode(blockInstanceId, resourceId)], [{
          content: [],
          blockInstanceId: blockInstanceId,
          resourceId: resourceId
        }]).then(function (results) {
          results.forEach(function (result) {
            var resourceId = result.resourceId;
            if (resourceId) {
              _this3.commitPendingCreation(true);
            } else {
              _this3.commitPendingCreation(false);
              // TODO: EDITOR-1921 - add error analytics
            }
          });
        }).catch(function (_reason) {
          _this3.commitPendingCreation(false);
          // TODO: EDITOR-1921 - add error analytics
        });
        this.registerPendingCreation(resourceId);
      } catch (error) {
        if (this.hasPendingCreation()) {
          this.commitPendingCreation(false);
        }
        // TODO: EDITOR-1921 - add error analytics
      }
    }
  }, {
    key: "deleteSyncBlocksWithConfirmation",
    value: function () {
      var _deleteSyncBlocksWithConfirmation = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(tr, syncBlockIds) {
        var _this4 = this;
        var confirmed, _this$editorView, results, callback;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              if (!this.confirmationCallback) {
                _context2.next = 22;
                break;
              }
              this.confirmationTransaction = tr;
              _context2.next = 4;
              return this.confirmationCallback(syncBlockIds.length);
            case 4:
              confirmed = _context2.sent;
              if (!confirmed) {
                _context2.next = 21;
                break;
              }
              (_this$editorView = this.editorView) === null || _this$editorView === void 0 || _this$editorView.dispatch(this.confirmationTransaction.setMeta('isConfirmedSyncBlockDeletion', true));
              _context2.prev = 7;
              if (this.dataProvider) {
                _context2.next = 10;
                break;
              }
              throw new Error('Data provider not set');
            case 10:
              syncBlockIds.forEach(function (Ids) {
                _this4.setPendingDeletion(Ids, true);
              });
              _context2.next = 13;
              return this.dataProvider.deleteNodesData(syncBlockIds.map(function (attrs) {
                return attrs.resourceId;
              }));
            case 13:
              results = _context2.sent;
              if (results.every(function (result) {
                return result.success;
              })) {
                callback = function callback(Ids) {
                  return _this4.syncBlockCache.delete(Ids.resourceId);
                };
              } else {
                callback = function callback(Ids) {
                  _this4.setPendingDeletion(Ids, false);
                };
                // TODO: EDITOR-1921 - add error analytics
              }
              syncBlockIds.forEach(callback);
              _context2.next = 21;
              break;
            case 18:
              _context2.prev = 18;
              _context2.t0 = _context2["catch"](7);
              syncBlockIds.forEach(function (Ids) {
                _this4.setPendingDeletion(Ids, false);
              });
              // TODO: EDITOR-1921 - add error analytics
            case 21:
              this.confirmationTransaction = undefined;
            case 22:
            case "end":
              return _context2.stop();
          }
        }, _callee2, this, [[7, 18]]);
      }));
      function deleteSyncBlocksWithConfirmation(_x, _x2) {
        return _deleteSyncBlocksWithConfirmation.apply(this, arguments);
      }
      return deleteSyncBlocksWithConfirmation;
    }()
  }, {
    key: "rebaseTransaction",
    value: function rebaseTransaction(incomingTr, state) {
      if (!this.confirmationTransaction) {
        return;
      }
      this.confirmationTransaction = _rebaseTransaction(this.confirmationTransaction, incomingTr, state);
    }
  }, {
    key: "destroy",
    value: function destroy() {
      this.syncBlockCache.clear();
      this.confirmationCallback = undefined;
      this.confirmationTransaction = undefined;
      this.pendingResourceId = undefined;
      this.creationCallback = undefined;
      this.dataProvider = undefined;
      this.editorView = undefined;
    }
  }]);
}();